import _ from 'lodash'
import React from 'react'
import {
  flatKey,
  formatter,
  normalizeName,
  PivotData,
  sortedColHeader,
  usFmt,
  usFmtPct,
} from '../../Utilities'

class TSVExportRenderer extends React.PureComponent {
  hasAccumulative = (arr1) => {
    let subArr1 = _.slice(arr1, 0, 1)
    let subArr2 = ['Proyecciones']
    return _.isEqual(subArr1, subArr2)
  }

  hasMonthly = (arr1) => {
    let subArr1 = _.slice(arr1, 0, 2)
    let subArr2 = ['Fecha', 'Proyecciones']
    return _.isEqual(subArr1, subArr2)
  }

  getBasePivotSettings() {
    const props = this.props
    const colAttrs = props.cols
    const rowAttrs = props.rows

    const tableOptions = Object.assign(
      {
        rowTotals: false,
        colTotals: true,
      },
      props.tableOptions,
    )

    const rowTotals = tableOptions.rowTotals || colAttrs.length === 0
    const colTotals = tableOptions.colTotals || rowAttrs.length === 0

    const subtotalOptions = Object.assign(
      {
        arrowCollapsed: '⨁',
        arrowExpanded: 'ⴱ',
      },
      props.subtotalOptions,
    )

    const colSubtotalDisplay = Object.assign(
      {
        displayOnTop: false,
        enabled: rowTotals,
        hideOnExpand: false,
      },
      subtotalOptions.colSubtotalDisplay,
    )

    const rowSubtotalDisplay = Object.assign(
      {
        displayOnTop: true,
        enabled: colTotals,
        hideOnExpand: false,
      },
      subtotalOptions.rowSubtotalDisplay,
    )

    const pivotData = new PivotData(props, {
      rowEnabled: rowSubtotalDisplay.enabled,
      colEnabled: colSubtotalDisplay.enabled,
      rowPartialOnTop: rowSubtotalDisplay.displayOnTop,
      colPartialOnTop: colSubtotalDisplay.displayOnTop,
    })

    const rowKeys = pivotData.getRowKeys()
    const colKeys = pivotData.getColKeys()
    // Also pre-calculate all the callbacks for cells, etc... This is nice to have to
    // avoid re-calculations of the call-backs on cell expansions, etc...
    const cellCallbacks = {}
    const rowTotalCallbacks = {}
    const colTotalCallbacks = {}
    let grandTotalCallback = null
    // Custom lines
    const linesOrder = !_.isEmpty(tableOptions?.linesOrder) ? tableOptions.linesOrder : []
    const linesTotal = !_.isEmpty(tableOptions?.linesTotal) ? tableOptions.linesTotal : []
    const linesRatio = !_.isEmpty(tableOptions?.linesRatio) ? tableOptions.linesRatio : []
    const linesRatioN = !_.isEmpty(tableOptions?.linesRatioN) ? tableOptions.linesRatioN : []
    const referenceBase = !_.isEmpty(tableOptions?.referenceBase)
      ? tableOptions.referenceBase
      : null
    const referenceBasePosition = this.hasAccumulative(colAttrs)
      ? 0
      : this.hasMonthly(colAttrs)
      ? 1
      : 0

    return Object.assign({
      pivotData,
      colAttrs,
      rowAttrs,
      colKeys,
      rowKeys,
      rowTotals,
      colTotals,
      arrowCollapsed: subtotalOptions.arrowCollapsed,
      arrowExpanded: subtotalOptions.arrowExpanded,
      colSubtotalDisplay,
      rowSubtotalDisplay,
      cellCallbacks,
      rowTotalCallbacks,
      colTotalCallbacks,
      grandTotalCallback,
      linesOrder,
      linesTotal,
      linesRatio,
      linesRatioN,
      referenceBase,
      referenceBasePosition,
    })
  }

  esInfinity(num) {
    return num === Infinity || num === -Infinity
  }

  getAggregator(pivotData, rowKey, colKey) {
    try {
      const agg = pivotData.getAggregator(rowKey, colKey)
      return agg.value()
    } catch (e) {
      return 0
    }
  }

  getFormatter(pivotData) {
    let formatterFunction = usFmt
    if (formatter.hasOwnProperty(pivotData.props.aggregatorName)) {
      formatterFunction = formatter[pivotData.props.aggregatorName]
    }
    return formatterFunction
  }

  calculateVariation(valueBase, valueReference, varitaion = 'positive') {
    let percentage_variation = 0
    let absolute_variation = this.normalizeNumber(valueBase, varitaion)

    if (valueReference !== 0.0) {
      absolute_variation =
        this.normalizeNumber(valueBase, varitaion) - this.normalizeNumber(valueReference, varitaion)
      if (isNaN(absolute_variation) || this.esInfinity(absolute_variation)) {
        absolute_variation = 0
      }

      percentage_variation =
        this.normalizeNumber(absolute_variation, varitaion) /
        this.normalizeNumber(valueReference, varitaion)
      if (isNaN(percentage_variation) || this.esInfinity(percentage_variation)) {
        percentage_variation = 0
      }
    }
    return [absolute_variation, percentage_variation]
  }

  normalizeNumber(value, variation = 'positive') {
    if (!value) {
      return 0
    }
    if (variation === 'negative') {
      value = Math.abs(value)
    }

    const normalizedValue = parseFloat(value).toFixed(6)
    return parseFloat(normalizedValue)
  }

  renderLinesRow(rowKey, rowIdx, pivotSettings, visibleRowKeys) {
    const { linesOrder, linesTotal, linesRatio, linesRatioN } = pivotSettings
    let isTree = false
    let flatRowKey = flatKey(rowKey)

    if (rowKey.length > 1) {
      flatRowKey = rowKey[0]
      isTree = true
    }
    const line = linesOrder?.find((it) => normalizeName(it.name) === flatRowKey)
    const lineTotal = linesTotal?.find((it) => normalizeName(it.name) === flatRowKey)
    const lineRatio = linesRatio?.find((it) => normalizeName(it.name) === flatRowKey)
    const lineRatioN = linesRatioN?.find((it) => normalizeName(it.name) === flatRowKey)
    const originalRowIdx = visibleRowKeys.findIndex((entry) => flatKey(entry) === flatKey(rowKey))

    if (lineTotal) {
      if (!isTree) {
        return this.renderTableTotalRow(rowKey, rowIdx, pivotSettings, lineTotal, line)
      }
    } else if (lineRatio) {
      if (!isTree) {
        return this.renderTableRatioRow(rowKey, rowIdx, pivotSettings, lineRatio, usFmtPct, line)
      }
    } else if (lineRatioN) {
      if (!isTree) {
        return this.renderTableRatioRow(rowKey, rowIdx, pivotSettings, lineRatioN, usFmt, line)
      }
    } else if (originalRowIdx < 0) {
      return this.renderTableLineZeroRow(rowKey, rowIdx, pivotSettings, line)
    } else {
      return this.renderTableRow(rowKey, originalRowIdx, pivotSettings)
    }
  }

  renderTableTotalRow(rowKey, rowIdx, pivotSettings, lineTotal, line) {
    const { visibleColKeys, pivotData, referenceBasePosition, referenceBase } = pivotSettings
    const formatter = this.getFormatter(pivotData)
    const variation = line?.calculation ?? 'positive'

    const lines = lineTotal.lines.reduce((mergedLines, it) => {
      return mergedLines.concat(it.lines)
    }, [])

    const attrValueCells = rowKey.map((r) => r)
    const rowLength = pivotData.props.rows.length - 1
    for (let i = 0; i < rowLength; i++) {
      attrValueCells.push('')
    }

    const valueCells = []
    visibleColKeys.forEach((colKey) => {
      const accumulator = lines.reduce((acc, it) => {
        const amount = this.getAggregator(pivotData, [normalizeName(it)], colKey)
        return acc + amount
      }, 0)

      const hasReferenceBase = colKey[referenceBasePosition] === referenceBase
      valueCells.push(formatter(accumulator))
      if (!hasReferenceBase) {
        const colKeyReferenceBase = _.cloneDeep(colKey)
        colKeyReferenceBase[referenceBasePosition] = referenceBase
        const accumulatorBase = lines.reduce((acc, it) => {
          const amount = this.getAggregator(pivotData, [normalizeName(it)], colKeyReferenceBase)
          return acc + amount
        }, 0)

        const [absolute_variation, percentage_variation] = this.calculateVariation(
          accumulatorBase,
          accumulator,
          variation,
        )

        valueCells.push(...[formatter(absolute_variation), usFmtPct(percentage_variation)])
      }
    })

    const accumulator = lines.reduce((acc, it) => {
      const amount = this.getAggregator(pivotData, [normalizeName(it)], [])
      return acc + amount
    }, 0)

    const totalCell = formatter(accumulator)

    return [...attrValueCells, ...valueCells, totalCell]
  }

  renderTableRatioRow(rowKey, rowIdx, pivotSettings, lineRatio, formatter = usFmt, line) {
    const { visibleColKeys, pivotData, referenceBase, referenceBasePosition } = pivotSettings
    let lines1 = []
    let lines2 = []
    if (!_.isEmpty(lineRatio.lines)) {
      lines1 = lineRatio.lines[0]
      lines2 = lineRatio.lines[1]
    }

    const variation = line?.calculation ?? 'positive'

    const attrValueCells = rowKey.map((r) => r)
    const rowLength = pivotData.props.rows.length - 1
    for (let i = 0; i < rowLength; i++) {
      attrValueCells.push('')
    }

    const valueCells = []
    visibleColKeys.forEach((colKey) => {
      let resultAccumulator = 0

      const accumulator1 = lines1.lines.reduce((acc, it) => {
        const amount = this.getAggregator(pivotData, [normalizeName(it)], colKey)
        return acc + amount
      }, 0)

      const accumulator2 = lines2.lines.reduce((acc, it) => {
        const amount = this.getAggregator(pivotData, [normalizeName(it)], colKey)
        return acc + amount
      }, 0)

      if (accumulator2 !== 0.0) {
        resultAccumulator = accumulator1 / accumulator2
      }
      valueCells.push(formatter(resultAccumulator))

      const hasReferenceBase = colKey[referenceBasePosition] === referenceBase

      if (!hasReferenceBase) {
        const colKeyReferenceBase = _.cloneDeep(colKey)
        colKeyReferenceBase[referenceBasePosition] = referenceBase

        let resultAccumulatorBase = 0
        const accumulator1Base = lines1.lines.reduce((acc, it) => {
          const amount = this.getAggregator(pivotData, [normalizeName(it)], colKeyReferenceBase)
          return acc + amount
        }, 0)

        const accumulator2Base = lines2.lines.reduce((acc, it) => {
          const amount = this.getAggregator(pivotData, [normalizeName(it)], colKeyReferenceBase)
          return acc + amount
        }, 0)

        if (accumulator2Base !== 0.0) {
          resultAccumulatorBase = accumulator1Base / accumulator2Base
        }

        const [absolute_variation, percentage_variation] = this.calculateVariation(
          resultAccumulatorBase,
          resultAccumulator,
          variation,
        )

        valueCells.push(...[formatter(absolute_variation), usFmtPct(percentage_variation)])
      }
    })

    let result = 0
    const accumulator1 = lines1.lines.reduce((acc, it) => {
      const amount = this.getAggregator(pivotData, [normalizeName(it)], [])
      return acc + amount
    }, 0)

    const accumulator2 = lines2.lines.reduce((acc, it) => {
      const amount = this.getAggregator(pivotData, [normalizeName(it)], [])
      return acc + amount
    }, 0)

    if (accumulator2 !== 0.0) {
      result = accumulator1 / accumulator2
    }

    const totalCell = formatter(result)

    return [...attrValueCells, ...valueCells, totalCell]
  }

  renderTableLineZeroRow(rowKey, rowIdx, pivotSettings, line) {
    const { visibleColKeys, pivotData, referenceBasePosition, referenceBase } = pivotSettings
    const formatter = this.getFormatter(pivotData)

    const attrValueCells = rowKey.map((r) => r)
    const rowLength = pivotData.props.rows.length - 1
    for (let i = 0; i < rowLength; i++) {
      attrValueCells.push('')
    }

    const valueCells = []
    visibleColKeys.forEach((colKey) => {
      const hasReferenceBase = colKey[referenceBasePosition] === referenceBase
      valueCells.push(formatter(0))
      if (!hasReferenceBase) {
        valueCells.push(...[formatter(0), usFmtPct(0)])
      }
    })

    const totalCell = formatter(0)

    return [...attrValueCells, ...valueCells, totalCell]
  }

  renderTableRow(rowKey, rowIdx, pivotSettings) {
    const { visibleColKeys, pivotData, referenceBase, referenceBasePosition } = pivotSettings
    const formatter = this.getFormatter(pivotData)

    const attrValueCells = rowKey.map((r) => r)

    let valueCells = []
    visibleColKeys.forEach((colKey) => {
      const agg = pivotData.getAggregator(rowKey, colKey)
      const aggValue = agg.value()
      const hasReferenceBase = colKey[referenceBasePosition] === referenceBase
      valueCells.push(formatter(aggValue))

      if (!hasReferenceBase) {
        const colKeyReferenceBase = _.cloneDeep(colKey)
        colKeyReferenceBase[referenceBasePosition] = referenceBase
        const aggBase = pivotData.getAggregator(rowKey, colKeyReferenceBase)
        const valueBase = aggBase.value()
        const [absolute_variation, percentage_variation] = this.calculateVariation(
          valueBase,
          aggValue,
        )

        valueCells.push(...[formatter(absolute_variation), usFmtPct(percentage_variation)])
      }
    })

    const agg = pivotData.getAggregator(rowKey, [])
    const aggValue = agg.value()
    const totalCell = agg.format(aggValue)

    return [...attrValueCells, ...valueCells, totalCell]
  }

  render() {
    if (this.cachedProps !== this.props) {
      this.cachedProps = this.props
      this.cachedBasePivotSettings = this.getBasePivotSettings()
    }
    const { rowKeys, colKeys, linesOrder, pivotData, referenceBase, referenceBasePosition } =
      this.cachedBasePivotSettings

    const sortedVisibleColKeys = sortedColHeader(referenceBase, colKeys, referenceBasePosition)

    const pivotSettings = Object.assign(
      {
        visibleRowKeys: rowKeys,
        visibleColKeys: sortedVisibleColKeys,
      },
      this.cachedBasePivotSettings,
    )

    if (rowKeys.length === 0) {
      rowKeys.push([])
    }
    if (colKeys.length === 0) {
      colKeys.push([])
    }

    const baseRow = _.cloneDeep(pivotData.props.rows)
    const headerRow = baseRow.map((r) => r)
    if (colKeys.length === 1 && colKeys[0].length === 0) {
      headerRow.push(this.props.aggregatorName)
    } else {
      sortedVisibleColKeys.forEach((c) => {
        const hasReferenceBase = c[referenceBasePosition] === referenceBase
        headerRow.push(c.join('-'))
        if (!hasReferenceBase) {
          headerRow.push('VAR $')
          headerRow.push('VAR %')
        }
      })
    }
    headerRow.push('Total')

    let visibleRowKeys = []
    linesOrder.forEach((it) => {
      if (it.type === 'grouper') {
        const listFilter = rowKeys.filter((entry) => it.name === entry[0])
        if (listFilter.length > 0) {
          const lastIndex = listFilter.length - 1
          const firstItem = listFilter[lastIndex].length
          const sortedFilter = listFilter.filter((entry) => entry.length === firstItem)
          visibleRowKeys.push(...sortedFilter)
        } else {
          visibleRowKeys.push([it.name])
        }
      } else {
        visibleRowKeys.push([it.name])
      }
    })

    const result = visibleRowKeys.map((r, i) => {
      return this.renderLinesRow(r, i, pivotSettings, rowKeys)
    })

    result.unshift(headerRow)

    return (
      <textarea
        value={result.map((r) => r.join('\t')).join('\n')}
        style={{ width: '100%', height: window.innerHeight / 2 }}
        readOnly={true}
      />
    )
  }
}

TSVExportRenderer.defaultProps = PivotData.defaultProps
TSVExportRenderer.propTypes = PivotData.propTypes

export default TSVExportRenderer
